import React, { useState, useEffect, useContext, useMemo } from "react";
import LeaderBoardShow from "./LeaderBoardShow";
import QuestContext from "../../../../components/QuestWrapper";
import LeaderBoardApi from "./LeaderBoardApi";
import {
  LeaderBoardProps,
  LeaderboardResponse,
  LeaderboardData,
  PartialBrandTheme,
  PartialQuestThemeData,
} from "./leaderBoardTypes";
import General, { useGeneral } from "../../../../general";
import {
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
} from "../../tutorial/components/tutorial.types";
import LeaderboardThemeManager from "./LeaderboardThemeManager";

const LeaderBoardWrapper: React.FC<LeaderBoardProps> = ({
  userId,
  token,
  styleConfig,
  uniqueEmailId,
  uniqueUserId,
  userLimit,
  userName,
  userImageUrl,
  FirstRankIconImage,
  SecondRankIconImage,
  ThirdRankIconImage,
  DefaultName = "Anonymous",
  DefaultImage,
  IsTopRank,
  isXpImage,
  rewardHead,
  setPresentUserData,
  presentUserRank,
  setPresentUserRank,
  campaignId,
  variation,
  isLoader,
  loaderComponent,
  entityAuthenticationToken,
  onUserClick,
  customUnit,
  customUnitImage
}) => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardResponse | null>(null);
  const [activeUserId, setActiveUserId] = useState(userId || "");
  const [activeToken, setActiveToken] = useState(token || "");
  const { apiKey, apiSecret, entityId, apiType, themeConfig, user } = useContext(QuestContext.Context);

  const [brandTheme, setBrandTheme] = useState<PartialBrandTheme>(DEFAULT_BRAND_THEME);
  const [questThemeData, setQuestThemeData] = useState<PartialQuestThemeData>(DEFAULT_QUEST_THEME);
  const [showFooterApi, setShowFooterApi] = useState(true);

  const leaderBoardApi = new LeaderBoardApi(apiType);
  const GeneralFunctions = new General("mixpanel", apiType);

  const themeManager = useMemo(() => {
    return new LeaderboardThemeManager(
      themeConfig,
      styleConfig,
      questThemeData,
      brandTheme
    );
  }, [themeConfig, styleConfig, questThemeData, brandTheme]);


  const general = useGeneral('quest')

  useEffect(() => {
    const initializeLeaderboard = async () => {
      GeneralFunctions.fireTrackingEvent("quest_leaderboard_loaded", "leaderboard");
      general.trackEvent(
        'quest-leaderboard-loaded',
        (uniqueUserId || userId || user.userId)!,
        {},
        true,
        !uniqueUserId,
        true
      )

      try {
        const userAuth = await leaderBoardApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId: activeUserId,
          token: activeToken,
          userName,
          userImageUrl,
          entityAuthenticationToken,
        });

        if (!userAuth) return;

        const { userId: newUserId, token: newToken } = userAuth;
        setActiveUserId(newUserId);
        setActiveToken(newToken);

        const headers = {
          apiKey,
          apiSecret,
          userId: newUserId,
          token: newToken,
        };

        const leaderboardResponse = await leaderBoardApi.getLeaderboard(entityId, headers, userLimit);

        if (!leaderboardResponse) return;

        if (campaignId) {
          const response = await leaderBoardApi.getLeaderBoardCampaignResults(
            entityId,
            campaignId,
            headers,
            variation
          );
          if (response?.brandTheme?.properties) {
            const newBrandTheme = response?.brandTheme?.properties;
            setBrandTheme(newBrandTheme);
            themeManager.updateBrandTheme(newBrandTheme);
          }
          if (response?.questThemeData) {
            setQuestThemeData(response?.questThemeData);
            themeManager.updateQuestTheme(response?.questThemeData);
          }

          setShowFooterApi(response?.showFooter !== undefined ? response.showFooter : true);
        }

        let userData = leaderboardResponse.data;
        let presentUserData: LeaderboardData | null = null;
        let presentUserRankIndex: number | null = null;

        leaderboardResponse.data = userData.map((item, index) => {
          item.name = item.name || DefaultName;
          if (item.userId === newUserId) {
            presentUserRankIndex = index + 1;
            presentUserData = item;
          }
          item.runningXP = formatNumber(item.runningXP as number);
          return item;
        });

        setLeaderboardData(leaderboardResponse);

        if (setPresentUserData) {
          if (presentUserData) {
            setPresentUserData(presentUserData);
          } else {
            const [xp, userDetails] = await Promise.all([
              leaderBoardApi.getXp(newUserId, headers, entityId),
              leaderBoardApi.getUserDetails(newUserId, headers),
            ]);

            if (userDetails) {
              setPresentUserData({
                userId: newUserId,
                name: userDetails.userName || DefaultName,
                imageUrl: userDetails.userImage || "",
                runningXP: xp || 0,
                externalUserId: uniqueUserId,
              });
            }
          }
        }

        if (setPresentUserRank) {
          setPresentUserRank(presentUserRankIndex);
        }
      } catch (error) {
        console.error("Error initializing leaderboard:", error);
        GeneralFunctions.captureSentryException(error);
      }
    };

    initializeLeaderboard();
  }, []);

  function formatNumber(num: number) {
    if (num === null || num === undefined || isNaN(num)) return "0";

    const absNum = Math.abs(num);
    let value, suffix = "";

    if (absNum >= 1e9) {
      value = num / 1e9;
      suffix = "B";
    } else if (absNum >= 1e6) {
      value = num / 1e6;
      suffix = "M";
    } else if (absNum >= 1e3) {
      value = num / 1e3;
      suffix = "K";
    } else {
      value = num;
    }

    const formatted =
      value % 1 === 0 ? value.toString() : value.toFixed(2);

    return formatted + suffix;
  }

  if (!leaderboardData) {
    if (isLoader) {
      return loaderComponent || <div>Loading...</div>
    } else {
      return null;
    }
  }

  return (
    <div style={{ background: "white", maxWidth: "500px", borderRadius: themeManager.getContainerStyles().borderRadius }}>
      {leaderboardData && (
        <LeaderBoardShow
          leaderboardUserData={leaderboardData}
          memberShip={leaderboardData.membershipsTiers}
          styleConfig={styleConfig}
          FirstRankIconImage={FirstRankIconImage}
          SecondRankIconImage={SecondRankIconImage}
          ThirdRankIconImage={ThirdRankIconImage}
          IsTopRank={IsTopRank}
          isXpImage={isXpImage}
          rewardHead={rewardHead}
          presentUserRank={presentUserRank}
          DefaultImage={DefaultImage}
          brandTheme={brandTheme}
          questThemeData={questThemeData}
          showFooter={showFooterApi}
          onUserClick={onUserClick}
          customUnit={customUnit}
          customUnitImage={customUnitImage}
        />
      )}
    </div>
  );
};

export default LeaderBoardWrapper;